<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg">
    <div class="tw-flex tw-flex-col">
      <c-button
        v-if="chats.length > SHOW_DEFAULT_CHAT_AMOUNT"
        :icon="
          !showAllChats
            ? 'fa-duotone fa-message-arrow-up'
            : 'fa-duotone fa-message-arrow-down'
        "
        outline
        size="md"
        :label="
          !showAllChats
            ? `Toon alle chats (${props.chats?.length || 0})`
            : 'Toon minder chats'
        "
        class="tw-mb-2 tw-self-center"
        @click="showAllChats = !showAllChats"
      />

      <ul v-auto-animate class="c-chat-screen">
        <li v-if="!formattedChats.length">
          <span
            class="tw-flex tw-px-4 tw-py-2 tw-text-sm tw-italic tw-text-slate-500"
          >
            Er zijn nog geen reacties...</span
          >
        </li>

        <li
          v-for="chat in formattedChats"
          :key="`${chat.name}-${chat.stamp}-${chat.text}`"
        >
          <c-chat-message :chat />
        </li>
      </ul>
    </div>

    <c-form
      v-if="!readonly"
      ref="chatInputForm"
      class="tw-flex tw-gap-x-2"
      @submit="addChat"
    >
      <c-input
        v-model="message"
        :autofocus
        class="tw-flex-1"
        dense
        bg-color="white"
        :disable="sending"
        :placeholder
        :rules="[required]"
        lazy-rules="ondemand"
        hide-bottom-space
      >
        <template #append>
          <q-icon
            :name="toClassicIcon(chatSendIcon, 'solid')"
            size="16px"
            color="dark"
            class="tw-cursor-pointer tw-transition-opacity hover:tw-opacity-70"
            @click="submitChatInputForm"
          />
        </template>
      </c-input>
    </c-form>

    <notification-message
      class="tw-my-4"
      :notification-key="notificationMessageContainer"
    />
  </div>
</template>

<script setup>
import { chatSendIcon, toClassicIcon } from "common/defaultIcons";
import { required } from "common/validationRules.js";
import NotificationMessage from "shared/components/NotificationMessage.vue";
import { useAuthStore } from "core/auth/auth.store";
import { useNotifyStore } from "store/modules/notifyStore.js";
import { storeToRefs } from "pinia";
import { computed, ref, useTemplateRef } from "vue";

// Notify store
const notifyStore = useNotifyStore();
const { setNotification } = notifyStore;

// Auth store — used to detect whether a chat was sent by the current user
const authStore = useAuthStore();
const { user: currentUser } = storeToRefs(authStore);

const props = defineProps({
  chats: {
    type: Array,
    required: true,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  callbackFunction: {
    type: Function,
    default: () => {},
  },
  extraParameters: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    required: false,
    default: "Plaats een reactie...",
  },
  /**
   * Turn off wherever the chat sits under something the user came to fill in: focusing an
   * input on mount also scrolls it into view, so the screen opens on the reactieveld rather
   * than on the form above it.
   */
  autofocus: {
    type: Boolean,
    default: true,
  },
});

// Refs
const chatInputFormRef = useTemplateRef("chatInputForm");
const submitChatInputForm = () => chatInputFormRef.value?.formRef.submit();

const notificationMessageContainer = computed(() => {
  return Math.random().toString(36).substring(2, 15);
});

const generateChatTimestamp = (isoDate) => {
  const dateObj = new Date(isoDate);
  const dateOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
  };
  const formattedDate = new Intl.DateTimeFormat("nl-NL", dateOptions).format(
    dateObj,
  );

  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  const formattedTime = new Intl.DateTimeFormat("nl-NL", timeOptions).format(
    dateObj,
  );

  return `${formattedDate} om ${formattedTime}`;
};

const SHOW_DEFAULT_CHAT_AMOUNT = 3;
const showAllChats = ref(false);
const mapChats = ({
  user_display_name,
  message,
  created_at,
  user_id,
  isSender = null,
  systemMessage = false,
  user,
}) => {
  return {
    name: user_display_name,
    text: [message],
    stamp: generateChatTimestamp(created_at),
    sent: isSender ?? currentUser.value?.id === user_id,
    avatar: "/images/empty-profile-pic.webp",
    systemMessage,
    user,
  };
};

const formattedChats = computed(() => {
  const { chats = [] } = props || {};

  if (!chats.length) {
    return [];
  }

  const showAmount = showAllChats.value
    ? props.chats.length
    : SHOW_DEFAULT_CHAT_AMOUNT;

  return props.chats.map(mapChats).slice(-showAmount).reverse();
});

const sending = ref(false);
const message = ref("");

const addChat = async () => {
  if (props.readonly || sending.value) {
    return;
  }

  try {
    sending.value = true;

    const chatData = {
      ...props.extraParameters,
      message: message.value,
    };

    await props.callbackFunction(chatData);

    message.value = "";

    setNotification({
      key: notificationMessageContainer.value,
      message: "Reactie geplaatst",
      type: "success",
    });
  } catch (error) {
    console.error(error);

    setNotification({
      key: notificationMessageContainer.value,
      message:
        error?.response?.data?.message ||
        "Er is iets fout gegaan bij het plaatsen van je reactie, probeer het nog eens",
      type: "error",
    });
  } finally {
    sending.value = false;
  }
};
</script>

<style>
.q-message-text--sent {
  @apply tw-bg-blue-100;

  &::before,
  &:last-child::before {
    @apply tw-border-b-blue-100;
  }
}

.q-message-stamp {
  font-size: 0.8rem;
  color: #525252;
  text-align: right;
}

.c-chat-screen {
  @apply tw-flex tw-flex-col-reverse tw-gap-2 tw-overflow-hidden tw-px-0 tw-py-2;
}
</style>
